import { get, writable } from 'svelte/store'
import { getProduct } from './product.js'
import { puzzlePack } from './puzzlePack.js'
import { subscription } from './subscription.js'
import { validateOrder } from './orders.js'
import { auth } from '../services/auth.js'
import { user } from '../models/user.js'
import { formatPrice } from '../services/utilities'

function createCart() {
  let syncing = false

  const syncCartData = async (cart, checkingOut) => {
    let results = []
    syncing = true

    for (let i of cart) {
      let product = await getProduct(i.sku)

      if (product) {
        product.changed = false
        product.changeMsgs = []

        // The product has gone out of stock!
        if (product.in_stock <= 0 && product.product_type.name !== 'Ticket') {
          // If checking out, drop the product
          if (checkingOut) continue

          // Otherwise, show the user an error message
          product.changed = true
          product.changeMsgs.push(
            'This product is unavailable or no longer in stock, please remove.',
          )

          // Check for stock limitations
        } else if (
          product.in_stock < i.quantity &&
          product.product_type.name !== 'Ticket'
        ) {
          product.changed = true
          product.quantity = product.in_stock
          product.changeMsgs.push(
            `The quantity has been reduced from ${i.quantity} to ${product.quantity} due to limited stock!`,
          )
        }

        // The price has changed!
        if (i.price !== product.price) {
          product.changed = true
          product.changeMsgs.push(
            `The price has changed from ${formatPrice(
              i.price,
            )} to ${formatPrice(product.price)}!`,
          )
        }

        // Woo hoo! It's on sale!
        if (i.sale_price == 0 && i.sale_price !== product.sale_price) {
          product.changed = true
          product.changeMsgs.push('This product is now on sale!')
        }

        if (product.product_type_id == 4) {
          puzzlePack.setInit(product.sku)
        }

        // Update the item
        results.push({ ...i, ...product })
      }
    }

    // Only update cart after everything is updated.
    cart = results
    syncing = false
    return cart
  }

  let validating = false
  const validateCart = async (cart) => {
    let isValid = null
    let error = ''

    // Don't run while the syncing or validating
    if (!validating) {
      isValid = true
      validating = true

      let data = {}
      data.customer_id = auth.getAuthenticatedUser()

      if (cart.length <= 0) {
        isValid = false
        error = 'You have no items in your cart!'
      } else {
        let order = await validateOrder(cart, data)

        if (order.error) {
          isValid = false
          error = order.error
        }

        // Puzzle Pack Validation. If the user has a puzzle pack in their cart,
        // make sure there's nothing funky about the amount of rentals.
        if (puzzlePack.isInit()) {
          let result = puzzlePack.checkItemCountValid()
          if (!result.valid) {
            isValid = false
            error = result.message
          }
        }

        // Check that the total quantity/stock is greater than zero
        let itemCount = cart.reduce(
          (a, c) => a + Math.min(c.quantity, c.in_stock),
          0,
        )

        // Tickets don't have in_stock quantity, so count individually
        cart.forEach((p) => {
          if (p.product_type.name === 'Ticket') {
            itemCount++
          }
        })

        if (isValid && itemCount === 0) {
          isValid = false
          error = 'You have no in stock items in your cart!'
        }

        validating = false
      }
    }

    return {
      valid: isValid,
      message: error,
    }
  }

  const saveCart = (cart) => {
    localStorage.setItem('cart', JSON.stringify(cart))
    return cart
  }

  const { subscribe, set, update } = writable([])

  return {
    subscribe,

    loadCart: async () => {
      // load puzzle pack details
      await puzzlePack.loadAll()

      var data = localStorage.getItem('cart') || []
      let subData = await user.checkSubscription()
      subscription.setSubscriptionDetails(subData)

      if (data && data.length != 0) {
        data = JSON.parse(data)
        if (data.length > 0) {
          // initialize puzzle pack or subscription rentals
          puzzlePack.setCount(data)
          subscription.setCount(data)
          update((cart) => saveCart(data))
        }
      }
    },

    addSubscriptionRental: (p) => {
      let success = true
      update((cart) => {
        if (cart.length === 0) {
          // subscription rental is now starting
          subscription.setInit(true)
        }
        if (!subscription.isFull()) {
          // if the subscription rental quota is not full
          // add item
          subscription.addItem()

          p.quantity = 1
          return saveCart([...cart, p])
        }
        success = false
        subscription.setError(
          'Your rental quota is full. Please remove an item from your cart, if you would like to add this item.',
        )
        return cart
      })
      return success
    },

    swapPuzzlePack: (pp) => {
      // check puzzle pack item count with the max of the requested puzzle pack swap
      if (!puzzlePack.checkCart(pp, 'swap')) return cart

      update((cart) => {
        for (let i in cart) {
          if (cart[i].product_type.name == 'Puzzle Pack') {
            pp.quantity = 1
            puzzlePack.setInit(pp.sku, puzzlePack.getItemCount())
            cart[i] = pp
            break
          }
        }
        return saveCart(cart)
      })
    },

    add: (p) => {
      // automatically adds puzzle pack if the user adds rental
      // TODO: probably should break this out similar to subscription for less coupling
      update((cart) => {
        if (!puzzlePack.checkCart(p, 'add')) return cart

        // only do this for rentals
        var isRental = p.product_type.name === 'Puzzle (Rental)'

        if (p.product_type.name === 'Puzzle Pack' && !puzzlePack.isInit()) {
          // this hits when just adding a puzzle pack to a cart
          puzzlePack.setInit(p.sku)
        }

        if (isRental) {
          if (puzzlePack.isFull()) return cart

          if (!puzzlePack.isInit()) {
            // add puzzle pack if its not already added
            let pp = get(puzzlePack).details
            pp.quantity = 1
            cart = saveCart([...cart, pp])
          }

          puzzlePack.addItem()
        }

        for (let c of cart) {
          if (c.id === p.id) {
            c.quantity++
            if (c.quantity > c.in_stock) {
              c.quantity--

              if (puzzlePack.isInit() && isRental) {
                puzzlePack.removeItem()
              }
              puzzlePack.setError(
                'The quantity you are requesting is more than what is in stock',
              )
              return saveCart(cart)
            }
            return saveCart(cart)
          }
        }

        p.quantity = 1
        return saveCart([...cart, p])
      })
    },

    remove: (p) => {
      update((cart) => {
        // prevent the user from removing the puzzle pack if they still have rentals
        if (!puzzlePack.checkCart(p, 'remove')) return cart

        for (let i in cart) {
          if (cart[i].id === p.id) {
            if (cart[i].quantity <= 1) {
              cart.splice(i, 1)
            } else {
              cart[i].quantity--
            }

            if (p.product_type.name === 'Puzzle (Rental)') {
              if (puzzlePack.isInit()) {
                puzzlePack.removeItem()
              } else if (subscription.isInit()) {
                subscription.removeItem()
              }
            }

            if (p.product_type.name === 'Puzzle Pack') {
              puzzlePack.reset()
            }

            return saveCart(cart)
          }
        }
      })
    },

    // Makes sure the cart data is up to date with the database
    syncCartData: async (cart, checkingOut = false) => {
      if (cart.length > 0) {
        cart = await syncCartData(cart, checkingOut)
        update(() => saveCart(cart))
      }
    },

    // Ensures cart is checkoutable. Checks prices, stock, and puzzle pack validity.
    validateCart: async (cart) => {
      return await validateCart(cart)
    },

    removeOutOfStock(products) {
      update((cart) => {
        for (let i in cart) {
          let p = products.find((x) => x.id === cart[i].id)
          if (p) {
            if (cart[i].quantity - p.requested_over_stock <= 0) {
              cart.splice(i, 1)
            } else {
              cart[i].quantity -= p.requested_over_stock
            }
          }
        }

        return saveCart(cart)
      })
    },

    hasPuzzlePack: () => {
      return puzzlePack.isInit()
    },

    hasMembershipInCart: () => {
      let hasSub = false
      subscribe((cart) => {
        for (let c of cart) {
          if (c.product_type.name === 'Subscription') {
            hasSub = true
            break
          }
        }
      })
      return hasSub
    },

    empty: () => {
      update((cart) => {
        cart = []
        return saveCart(cart)
      })
      puzzlePack.reset()
    },
  }
}

export let cart = createCart()
